import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import Reveal from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import trattamentiHeroImg from "@/assets/hero-trattamenti.webp";
import { ayurvedicTreatments, otherTreatments, Treatment } from "@/data/treatments";

const sections: { title: string; subtitle: string; items: Treatment[] }[] = [
  {
    title: "Trattamenti Ayurvedici",
    subtitle: "La tradizione millenaria dell'Ayurveda, su misura per te",
    items: ayurvedicTreatments,
  },
  {
    title: "Altri Trattamenti",
    subtitle: "Discipline olistiche e di benessere naturale",
    items: otherTreatments,
  },
];

const TreatmentCard = ({ t }: { t: Treatment }) => (
  <Link to={`/trattamenti/${t.slug}`} className="group block h-full">
    <article className="h-full bg-card rounded-2xl overflow-hidden border border-border shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      <div className="aspect-[4/3] overflow-hidden">
        <img
          src={t.image}
          alt={t.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-5">
        <h3 className="font-serif text-2xl text-primary group-hover:text-accent transition-colors">
          {t.name}
        </h3>
        <p className="mt-2 text-sm text-foreground/75 leading-relaxed line-clamp-3">
          {t.short}
        </p>
      </div>
    </article>
  </Link>
);

const TreatmentsIndex = () => {
  return (
    <Layout>
      <SEOHead
        title="Trattamenti Ayurvedici e Olistici | Studio Olistico ORIGINI"
        description="Scopri tutti i trattamenti ayurvedici, olistici e per il viso offerti da Studio Olistico ORIGINI: Shirodhara, massaggi, cristalloterapia e molto altro a Montemerlo (PD)."
        url="/trattamenti"
      />

      {/* Header image dedicata ai trattamenti ayurvedici (senza testo) */}
      <section className="relative h-[46vh] min-h-[300px] overflow-hidden">
        <img
          src={trattamentiHeroImg}
          alt="Massaggio ayurvedico con olio caldo — Studio Olistico ORIGINI"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* velatura leggera in alto per la leggibilità della navigazione */}
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/35 to-transparent" />
        {/* sfumatura in basso verso lo sfondo pagina */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background to-transparent" />
      </section>

      {/* Titolo pagina + riga di orientamento (sotto l'immagine) */}
      <Reveal className="container mx-auto px-4 pt-12 text-center max-w-2xl">
        <p className="eyebrow">Tutti i trattamenti</p>
        <h1 className="mt-3 font-serif text-5xl md:text-7xl text-primary">
          Un percorso di benessere
        </h1>
        <p className="mt-5 text-lg text-foreground/80 leading-relaxed">
          Ayurveda, riflessologia plantare e trattamenti olistici, scelti su misura per te.
        </p>
      </Reveal>

      {/* Griglie per categoria */}
      <div className="container mx-auto px-4 py-16">
        {sections.map((section) => (
          <div key={section.title} className="mb-20 last:mb-0">
            <Reveal className="text-center mb-12">
              <h2 className="font-serif text-4xl md:text-5xl text-primary mb-2">
                {section.title}
              </h2>
              <p className="text-muted-foreground">{section.subtitle}</p>
              <div className="w-20 h-0.5 bg-accent mx-auto rounded-full mt-5" />
            </Reveal>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {section.items.map((t, i) => (
                <Reveal key={t.slug} delay={(i % 4) * 70}>
                  <TreatmentCard t={t} />
                </Reveal>
              ))}
            </div>
          </div>
        ))}

        {/* CTA */}
        <Reveal className="mt-20">
          <div className="text-center bg-gradient-to-br from-secondary/60 to-secondary/20 rounded-3xl p-8 md:p-12 border border-border">
            <h3 className="font-serif text-3xl md:text-4xl text-primary mb-4">
              Non sai quale trattamento scegliere?
            </h3>
            <p className="text-foreground/80 mb-8 text-lg max-w-2xl mx-auto">
              Contattami per una consulenza personalizzata e scopriremo insieme il percorso
              più adatto a te.
            </p>
            <Button
              asChild
              size="lg"
              className="text-base px-8 md:px-10 py-6 rounded-full hover:scale-105 transition-transform"
            >
              <a href="https://wa.me/393461533000" target="_blank" rel="noopener noreferrer">
                Prenota una consulenza
              </a>
            </Button>
          </div>
        </Reveal>
      </div>
    </Layout>
  );
};

export default TreatmentsIndex;
